import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { leaveAPI } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { formatDate, getStatusColor } from '../../lib/utils';
import { Calendar, Plus, Loader2, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { Progress } from '../../components/ui/progress';

const MyLeaves = () => {
  const { user } = useAuth();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newRequest, setNewRequest] = useState({
    leave_type_id: '',
    start_date: '',
    end_date: '',
    reason: ''
  });

  const fetchData = useCallback(async () => {
    if (!user?.company_id || !user?.id) return;
    try {
      const [typesRes, requestsRes, balancesRes] = await Promise.all([
        leaveAPI.getTypes(user.company_id),
        leaveAPI.getRequests(user.company_id),
        leaveAPI.getMyBalances(user.id)
      ]);
      setLeaveTypes(typesRes.data);
      setRequests(requestsRes.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setLeaveBalances(balancesRes.data || []);
    } catch (err) {
      toast.error('Failed to load leaves');
    } finally {
      setLoading(false);
    }
  }, [user?.company_id, user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    
    // Check balance before submitting
    const balance = getBalanceForType(newRequest.leave_type_id);
    const requestedDays = calculateDays(newRequest.start_date, newRequest.end_date);
    if (balance && requestedDays > balance.available_days) {
      toast.error(`Insufficient leave balance. You have ${balance.available_days} days available.`);
      return;
    }
    
    setSubmitting(true);
    try {
      await leaveAPI.createRequest(newRequest);
      toast.success('Leave request submitted');
      setIsAddOpen(false);
      setNewRequest({
        leave_type_id: '',
        start_date: '',
        end_date: '',
        reason: ''
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit leave request');
    } finally {
      setSubmitting(false);
    }
  };

  const getLeaveTypeName = (typeId) => {
    const type = leaveTypes.find(t => t.id === typeId);
    return type?.name || typeId;
  };
  
  const getBalanceForType = (typeId) => {
    return leaveBalances.find(b => b.leave_type_id === typeId);
  };
  
  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };
  
  const selectedBalance = getBalanceForType(newRequest.leave_type_id);
  const requestedDays = calculateDays(newRequest.start_date, newRequest.end_date);
  const hasInsufficientBalance = selectedBalance && requestedDays > selectedBalance.available_days;

  if (loading) {
    return (
      <DashboardLayout title="My Leaves">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Calculate stats
  const pending = requests.filter(r => r.status === 'pending').length;
  const approved = requests.filter(r => r.status === 'approved').length;
  const rejected = requests.filter(r => r.status === 'rejected').length;

  return (
    <DashboardLayout 
      title="My Leaves" 
      subtitle="Request and track your leave applications"
    >
      {/* Leave Balances Section */}
      <Card className="mb-6" data-testid="leave-balances-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5" />
            Leave Balances ({new Date().getFullYear()})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {leaveBalances.length === 0 ? (
            <p className="text-sm text-muted-foreground">No leave balances configured</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {leaveBalances.map((balance) => {
                const totalAllowed = balance.allocated_days + balance.carried_over + balance.adjustment_days;
                const usedPercent = totalAllowed > 0 ? ((balance.used_days + balance.pending_days) / totalAllowed) * 100 : 0;
                return (
                  <div 
                    key={balance.id || balance.leave_type_id} 
                    className="p-4 rounded-lg border bg-card"
                    data-testid={`balance-${balance.leave_type_code}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{balance.leave_type_name}</span>
                      <Badge variant={balance.is_paid ? "default" : "secondary"} className="text-xs">
                        {balance.is_paid ? "Paid" : "Unpaid"}
                      </Badge>
                    </div>
                    <div className="text-2xl font-bold text-primary mb-1">
                      {balance.available_days} <span className="text-sm font-normal text-muted-foreground">days available</span>
                    </div>
                    <Progress value={usedPercent} className="h-2 mb-2" />
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                        Used: {balance.used_days}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3 text-amber-500" />
                        Pending: {balance.pending_days}
                      </span>
                    </div>
                    {(balance.carried_over > 0 || balance.adjustment_days !== 0) && (
                      <div className="mt-2 pt-2 border-t text-xs text-muted-foreground">
                        {balance.carried_over > 0 && <span>+{balance.carried_over} carried over</span>}
                        {balance.adjustment_days !== 0 && (
                          <span className="ml-2">{balance.adjustment_days > 0 ? '+' : ''}{balance.adjustment_days} adjustment</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="stat-card">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-amber-600">{pending}</p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-emerald-600">{approved}</p>
            <p className="text-xs text-muted-foreground">Approved</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-red-600">{rejected}</p>
            <p className="text-xs text-muted-foreground">Rejected</p>
          </CardContent>
        </Card>
      </div>

      {/* Request Button */}
      <div className="flex justify-end mb-6">
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="request-leave-btn">
              <Plus className="h-4 w-4" />
              Request Leave
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Request Leave</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitRequest} className="space-y-4">
              <div className="space-y-2">
                <Label>Leave Type</Label>
                <Select
                  value={newRequest.leave_type_id}
                  onValueChange={(value) => setNewRequest({...newRequest, leave_type_id: value})}
                >
                  <SelectTrigger data-testid="leave-type-select">
                    <SelectValue placeholder="Select leave type" />
                  </SelectTrigger>
                  <SelectContent>
                    {leaveTypes.map(type => {
                      const bal = getBalanceForType(type.id);
                      return (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name} ({bal ? `${bal.available_days} days available` : `${type.days_per_year} days/year`})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {/* Show selected leave type balance info */}
                {selectedBalance && (
                  <div className="p-3 bg-muted rounded-md mt-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Available Balance:</span>
                      <span className="font-semibold text-primary">{selectedBalance.available_days} days</span>
                    </div>
                    {selectedBalance.pending_days > 0 && (
                      <div className="flex items-center justify-between text-xs mt-1">
                        <span className="text-muted-foreground">Pending Requests:</span>
                        <span className="text-amber-600">{selectedBalance.pending_days} days</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={newRequest.start_date}
                    onChange={(e) => setNewRequest({...newRequest, start_date: e.target.value})}
                    required
                    data-testid="leave-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={newRequest.end_date}
                    onChange={(e) => setNewRequest({...newRequest, end_date: e.target.value})}
                    required
                    data-testid="leave-end-date"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Reason</Label>
                <Textarea
                  value={newRequest.reason}
                  onChange={(e) => setNewRequest({...newRequest, reason: e.target.value})}
                  placeholder="Brief reason for leave"
                  required
                  data-testid="leave-reason-input"
                />
              </div>
              
              {/* Balance warning */}
              {requestedDays > 0 && selectedBalance && (
                <div className={`p-3 rounded-md flex items-center gap-2 text-sm ${hasInsufficientBalance ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                  {hasInsufficientBalance ? (
                    <>
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <span>Insufficient balance. Requesting {requestedDays} days but only {selectedBalance.available_days} available.</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                      <span>Requesting {requestedDays} day{requestedDays > 1 ? 's' : ''}. Balance after approval: {selectedBalance.available_days - requestedDays} days.</span>
                    </>
                  )}
                </div>
              )}
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={submitting || hasInsufficientBalance}
                data-testid="submit-leave-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Leave Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Leave Requests
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead className="hidden sm:table-cell">Start</TableHead>
                  <TableHead className="hidden sm:table-cell">End</TableHead>
                  <TableHead className="sm:hidden">Dates</TableHead>
                  <TableHead className="hidden md:table-cell">Reason</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id} data-testid={`leave-row-${request.id}`}>
                    <TableCell className="font-medium">{getLeaveTypeName(request.leave_type_id)}</TableCell>
                    <TableCell className="hidden sm:table-cell">{formatDate(request.start_date)}</TableCell>
                    <TableCell className="hidden sm:table-cell">{formatDate(request.end_date)}</TableCell>
                    <TableCell className="sm:hidden text-xs">
                      {formatDate(request.start_date)} - {formatDate(request.end_date)}
                    </TableCell>
                    <TableCell className="hidden md:table-cell max-w-xs truncate">{request.reason}</TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(request.status)}>
                        {request.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {requests.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No leave requests yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default MyLeaves;
